import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Send, Sparkles, User, CheckCircle, AlertCircle, PlayCircle, Loader, Check, CheckCheck, ChevronDown } from 'lucide-react';

const ThreadView = ({ thread, onThreadUpdate }) => {
    const [messages, setMessages] = useState([]);
    const [replyText, setReplyText] = useState('');
    const [loading, setLoading] = useState(false);
    const [humanHandling, setHumanHandling] = useState(thread?.human_handling || false);
    const [hasNewMessages, setHasNewMessages] = useState(false);
    const scrollContainerRef = useRef(null);
    const isAtBottomRef = useRef(true);
    const previousMessagesLengthRef = useRef(0);
    const messagesEndRef = useRef(null);

    const fetchMessages = React.useCallback(async () => {
        try {
            // Re-using existing message endpoint
            // We might want to migrate this to /api/threads/:id/messages eventually
            const response = await axios.get(`/api/messages/thread/${thread.thread_id}`);
            setMessages(response.data);
        } catch (err) {
            console.error("Failed to fetch messages", err);
        }
    }, [thread]);

    useEffect(() => {
        if (thread) {
            fetchMessages();
            setHumanHandling(thread.human_handling || false);

            // Set up polling for real-time updates (every 5 seconds)
            const interval = setInterval(fetchMessages, 5000);
            return () => clearInterval(interval);
        }
    }, [thread, fetchMessages]);

    useEffect(() => {
        if (messages.length > 0) {
            if (messages.length > previousMessagesLengthRef.current) {
                const isInitialLoad = previousMessagesLengthRef.current === 0;

                if (isInitialLoad || isAtBottomRef.current) {
                    scrollToBottom();
                } else {
                    setHasNewMessages(true);
                }
            }
        }
        previousMessagesLengthRef.current = messages.length;
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        setHasNewMessages(false);
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            // 100px threshold to trigger auto-scroll
            const isBottom = scrollHeight - scrollTop - clientHeight < 100;
            isAtBottomRef.current = isBottom;

            if (isBottom && hasNewMessages) {
                setHasNewMessages(false);
            }
        }
    };

    // Take over the conversation - pause AI responses
    const handleTakeOver = async () => {
        try {
            await axios.patch(`/api/threads/${thread.thread_id}`, { human_handling: true });
            setHumanHandling(true);
            if (onThreadUpdate) onThreadUpdate();
        } catch (err) {
            console.error(err);
            alert('Failed to take over conversation');
        }
    };

    // Resume AI responses
    const handleResumeAI = async () => {
        try {
            await axios.patch(`/api/threads/${thread.thread_id}`, { human_handling: false });
            setHumanHandling(false);
            if (onThreadUpdate) onThreadUpdate();
        } catch (err) {
            console.error(err);
            alert('Failed to resume AI');
        }
    };

    const handleResolve = async () => {
        try {
            // Resolve the ticket and also release human handling so AI can work on new messages
            await axios.patch(`/api/threads/${thread.thread_id}`, {
                status: 'resolved',
                human_handling: false
            });
            setHumanHandling(false);
            if (onThreadUpdate) onThreadUpdate();
            alert('Ticket resolved!');
        } catch (err) {
            console.error(err);
        }
    };

    const handleSend = async () => {
        if (!replyText.trim()) return;
        setLoading(true);
        try {
            // When agent sends a message, automatically enable human handling
            if (!humanHandling) {
                await axios.patch(`/api/threads/${thread.thread_id}`, { human_handling: true });
                setHumanHandling(true);
            }

            await axios.post('/api/messages', {
                threadId: thread.thread_id,
                chatbotId: thread.chatbot_id,
                role: 'assistant', // Agent reply
                content: replyText
            });

            // Refresh messages
            setReplyText('');
            isAtBottomRef.current = true;
            fetchMessages();
        } catch (err) {
            console.error(err);
            alert('Failed to send reply');
        } finally {
            setLoading(false);
        }
    };

    const [draftLoading, setDraftLoading] = useState(false);

    const handleMagicDraft = async () => {
        setDraftLoading(true);
        try {
            const res = await axios.post('/api/copilot/draft', {
                threadId: thread.thread_id,
                chatbotId: thread.chatbot_id
            });
            setReplyText(res.data.draft);
        } catch (err) {
            console.error(err);
            alert('Failed to generate draft');
        } finally {
            setDraftLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-background relative">
            {/* Header */}
            <div className="h-16 px-6 border-b border-border flex items-center justify-between shrink-0 bg-background/95 backdrop-blur z-10">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
                        {thread.customer_email ? thread.customer_email[0].toUpperCase() : 'V'}
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">
                            {thread.customer_email || 'Visitor'}
                        </h3>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${thread.status === 'open' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                            <span className="text-xs text-muted-foreground capitalize">{thread.status}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {!humanHandling ? (
                        <button
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors"
                            onClick={handleTakeOver}
                            title="Take over this conversation to respond manually. AI will stop responding."
                        >
                            <User size={14} />
                            Take Over
                        </button>
                    ) : (
                        <button
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                            onClick={handleResumeAI}
                            title="Allow AI to respond to this conversation again."
                        >
                            <PlayCircle size={14} />
                            Resume AI
                        </button>
                    )}
                    <button
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent hover:border-border rounded-md transition-all"
                        onClick={handleResolve}
                    >
                        <CheckCircle size={14} />
                        Resolve
                    </button>
                </div>
            </div>

            {humanHandling && (
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center justify-center gap-2 text-xs font-medium text-amber-800">
                    <AlertCircle size={14} />
                    You are handling this conversation. AI responses are paused.
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 relative min-h-0 flex flex-col">
                <div
                    className="flex-1 overflow-y-auto p-6 space-y-6"
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                >
                    {messages.map((msg, idx) => {
                        const isAgent = msg.role === 'assistant';
                        return (
                            <div key={idx} className={`flex w-full ${isAgent ? 'justify-end' : 'justify-start'}`}>
                                <div className={`
                                max-w-[70%] rounded-2xl p-4 shadow-sm relative group
                                ${isAgent
                                        ? 'bg-blue-50/50 border border-blue-100/50 rounded-tr-sm text-foreground'
                                        : 'bg-card border border-border rounded-tl-sm text-foreground'
                                    }
                            `}>
                                    <div className="prose prose-sm max-w-none text-sm leading-relaxed">
                                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                                    </div>
                                    <div className={`
                                    text-[10px] text-muted-foreground/60 mt-1.5 flex items-center gap-1
                                    ${isAgent ? 'justify-end' : 'justify-start'}
                                `}>
                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}

                                        {/* Read Receipt */}
                                        {isAgent && msg.seen_at && (
                                            <CheckCheck size={14} className="text-blue-500 ml-1" /> // Double blue check
                                        )}
                                        {isAgent && !msg.seen_at && (
                                            <Check size={14} className="text-muted-foreground/40 ml-1" /> // Singe gray check (sent but not seen) - optional, adding for completeness
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>

                {hasNewMessages && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
                        <button
                            onClick={scrollToBottom}
                            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-full shadow-lg hover:bg-blue-700 transition-all border border-blue-500 animate-in fade-in slide-in-from-bottom-2 duration-200"
                        >
                            New message <ChevronDown size={14} />
                        </button>
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-background border-t border-border mt-auto">
                <div className="relative flex flex-col bg-card border border-border rounded-xl shadow-sm focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
                    <textarea
                        className="w-full min-h-[80px] p-3 text-sm bg-transparent border-none rounded-t-xl focus:ring-0 resize-none placeholder:text-muted-foreground/50"
                        placeholder={humanHandling ? "Type a reply..." : "Type a reply... (This will activate Human handling)"}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                    />

                    <div className="flex items-center justify-between px-2 pb-2 mt-1">
                        <button
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all
                                ${draftLoading
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'text-purple-600 hover:bg-purple-50'
                                }
                            `}
                            onClick={handleMagicDraft}
                            disabled={loading || draftLoading}
                        >
                            {draftLoading ? <Loader size={12} className="animate-spin" /> : <Sparkles size={14} />}
                            {draftLoading ? 'Drafting...' : 'Magic Draft'}
                        </button>

                        <button
                            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-primary rounded-lg transition-all
                                ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary/90 shadow-sm'}
                            `}
                            onClick={handleSend}
                            disabled={loading}
                        >
                            {loading ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
                            {loading ? 'Sending...' : 'Send Reply'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThreadView;

